import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Search,
  Loader2,
  TableOfContents,
  Database,
  CheckCircle2,
  AlertTriangle,
  X,
  TrendingUp,
  Activity,
  Layers,
  Zap,
  BarChart2,
  RefreshCw,
} from 'lucide-react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts';
import baseapi from '../../../services/baseapi';
import { Pagination } from '../../InternalScan/InternalScanDashboard/Pagination';
import MultiSelectApiDropdownWithAll from '../../../components/MultiSelectApiDropdownWithAll';
import { CustomDropdown } from '../../../components/CustomDropdown ';
import { DBExportModal, type ColumnItem } from './DBExportModal';

const indexExportColumns: ColumnItem[] = [
  { key: 'database', label: 'Database' },
  { key: 'schema', label: 'Schema' },
  { key: 'table', label: 'Table' },
  { key: 'index', label: 'Index Name' },
  { key: 'idx_scans', label: 'Scans' },
  { key: 'idx_tup_read', label: 'Tuples Read' },
  { key: 'idx_tup_fetch', label: 'Tuples Fetch' },
  { key: 'idx_blks_read', label: 'Blocks Read' },
  { key: 'idx_blks_hit', label: 'Blocks Hit' },
  { key: 'cache_hit_pct', label: 'Cache Hit %' },
  { key: 'rows_per_scan', label: 'Rows / Scan' },
  { key: 'status', label: 'Status' },
];

interface IndexRow {
  database: string;
  schema: string;
  table: string;
  index: string;
  idx_scans: number;
  idx_tup_read: number;
  idx_tup_fetch: number;
  idx_blks_read: number;
  idx_blks_hit: number;
  last_seen: string;
  cache_hit_pct: number | null;
  rows_per_scan: number | null;
  unused: boolean;
}

interface IndexesViewProps {
  activeCluster: string;
  timeRange?: string;
  clientId?: string;
  refreshKey?: number;
  dbType?: string;
}

const PALETTE = [
  '#f97316', // orange
  '#3b82f6', // blue
  '#10b981', // emerald
  '#8b5cf6', // purple
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#f59e0b', // amber
  '#14b8a6', // teal
  '#6366f1', // indigo
  '#84cc16', // lime
  '#e11d48', // rose
  '#d946ef', // fuchsia
];

const ScrollableLegend = (props: any) => {
  const { payload, hoveredSeries, onHoverSeries } = props;
  if (!payload || payload.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 max-h-16 overflow-y-auto custom-scroll px-2 py-1 relative z-0">
      {payload.map((entry: any, index: number) => {
        const seriesKey = entry.dataKey || entry.value;
        const isHovered = hoveredSeries === seriesKey;
        const isDimmed = hoveredSeries !== null && !isHovered;
        return (
          <div
            key={`legend-item-${index}`}
            onMouseEnter={() => onHoverSeries && onHoverSeries(seriesKey)}
            onMouseLeave={() => onHoverSeries && onHoverSeries(null)}
            className={`flex items-center gap-1.5 shrink-0 cursor-pointer transition-all duration-200 ${isDimmed ? 'opacity-30 scale-95' : 'opacity-100 scale-95'
              }`}
          >
            <span
              className="w-2.5 h-2.5 rounded-full inline-block shrink-0 shadow-sm"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-gray-700 dark:text-gray-200 text-[10px] truncate max-w-[140px] font-mono" title={entry.value}>
              {entry.value}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const ChartTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border border-gray-200 dark:border-gray-700/80 rounded-lg p-2.5 shadow-xl text-[11px] text-gray-900 dark:text-gray-100 flex flex-col gap-1.5 min-w-[130px] z-50">
        {label !== undefined && label !== null && label !== "" && (
          <div className="text-gray-700 dark:text-gray-300 font-bold border-b border-gray-100 dark:border-gray-800 pb-1 mb-0.5 text-[11px] truncate">
            {label}
          </div>
        )}
        {payload.map((entry: any, index: number) => (
          <div key={index} className="flex justify-between items-center gap-4">
            <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-300 font-medium">
              <span
                className="inline-block w-2 h-2 rounded-full shrink-0"
                style={{ backgroundColor: entry.color || entry.stroke || entry.fill || "#f97316" }}
              />
              {entry.name || entry.dataKey}
            </span>
            <span className="font-bold text-gray-900 dark:text-white font-mono">
              {typeof entry.value === 'number' ? entry.value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }) : (entry.value ?? '-')}
            </span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};


export const IndexesView: React.FC<IndexesViewProps> = ({
  activeCluster,
  timeRange = '60',
  clientId,
  refreshKey,
  dbType,
}) => {
  const [loading, setLoading] = useState(true);
  const [graphLoading, setGraphLoading] = useState(false);
  const [indexData, setIndexData] = useState<IndexRow[]>([]);
  const [graphData, setGraphData] = useState<any>(null);
  const [selectedIndexFilter, setSelectedIndexFilter] = useState<string>('ALL');
  const [hoveredSeries, setHoveredSeries] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'used' | 'unused'>('all');
  const [selectedDatabases, setSelectedDatabases] = useState<any[]>([{ id: '*', name: 'Select all' }]);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  const effectiveDbType = (dbType || '').toLowerCase();
  const isMongo = ['mongodb', 'mongo'].includes(effectiveDbType) || effectiveDbType.includes('mongo') || (activeCluster || '').toLowerCase().includes('mongo');
  const isPostgres = ['postgres', 'postgresql'].includes(effectiveDbType) || effectiveDbType.includes('postgres') || (activeCluster || '').toLowerCase().includes('postgres') || (activeCluster || '').toLowerCase().includes('pg');
  const isMSSQL = ['mssql', 'sqlserver', 'sql_server'].includes(effectiveDbType) || effectiveDbType.includes('mssql') || (activeCluster || '').toLowerCase().includes('mssql');
  const isES = ['opensearch', 'elasticsearch'].includes(effectiveDbType) || effectiveDbType.includes('opensearch') || effectiveDbType.includes('elastic');
  const isClickHouse = ['clickhouse'].includes(effectiveDbType) || effectiveDbType.includes('clickhouse') || (activeCluster || '').toLowerCase().includes('clickhouse');

  const isMySQL =
    !isMongo &&
    !isPostgres &&
    !isMSSQL &&
    !isES &&
    !isClickHouse &&
    (['mysql', 'mariadb'].includes(effectiveDbType) ||
      effectiveDbType.includes('mysql') ||
      (activeCluster || '').toLowerCase().includes('mysql'));

  const isMariaDB = effectiveDbType.includes('mariadb')

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch Index Usage Graph Data ONLY for MySQL
  const fetchIndexGraph = useCallback(async () => {
    if (!activeCluster || !isMySQL) {
      setGraphData(null);
      return;
    }
    setGraphLoading(true);

    const minutes = parseInt(timeRange || '60', 10);
    const params: any = {
      cluster_id: activeCluster,
      minutes,
    };
    if (clientId) params.client_id = clientId;

    try {
      const res = await baseapi.get('/db_monitor_dashboard/graph/index_usage', { params });
      setGraphData(res.data?.data || res.data);
    } catch (err) {
      console.warn('IndexesView graph fetch error:', err);
      setGraphData(null);
    } finally {
      setGraphLoading(false);
    }
  }, [activeCluster, timeRange, clientId, isMySQL]);

  useEffect(() => {
    fetchIndexGraph();
  }, [fetchIndexGraph, refreshKey]);

  const fetchIndexTable = useCallback(async () => {
    if (!activeCluster) return;
    setLoading(true);

    const skip = (currentPage - 1) * itemsPerPage;
    const limit = itemsPerPage;

    const params: any = {
      cluster_id: activeCluster,
      skip,
      limit,
    };
    if (clientId) params.client_id = clientId;

    // API-level query filter for unused / active status
    if (statusFilter === 'unused') {
      params.unused = 'true';
      params.status = 'unused';
    } else if (statusFilter === 'used') {
      params.unused = 'false';
      params.status = 'used';
    }

    // Database filtering
    const dbNames = selectedDatabases
      .map((item) => (typeof item === 'string' ? item : item?.id || item?.name || item?.database))
      .filter((name) => name && name !== '*');

    if (dbNames.length > 0) {
      params.database = dbNames.join(',');
    }

    if (debouncedSearch.trim()) {
      params.search = debouncedSearch.trim();
    }

    try {
      const res = await baseapi.get('/db_monitor_dashboard/indexes/table', { params });
      const rawData = res.data?.data || [];
      const total =
        res.data?.total ??
        res.data?.count ??
        (rawData.length < limit && currentPage === 1
          ? rawData.length
          : res.data?.total_count ??
          currentPage * itemsPerPage + (rawData.length === limit ? 1 : 0));

      setIndexData(Array.isArray(rawData) ? rawData : []);
      if (res.data?.total !== undefined) {
        setTotalItems(res.data.total);
      } else if (res.data?.count !== undefined) {
        setTotalItems(res.data.count);
      } else {
        setTotalItems(total);
      }
    } catch (err) {
      console.warn('IndexesView fetch error:', err);
      setIndexData([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [activeCluster, clientId, currentPage, itemsPerPage, statusFilter, debouncedSearch, selectedDatabases]);

  useEffect(() => {
    fetchIndexTable();
  }, [fetchIndexTable, refreshKey]);

  const handleStatusFilterChange = (newStatus: 'all' | 'used' | 'unused') => {
    setStatusFilter(newStatus);
    setCurrentPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleItemsPerPageChange = (n: number) => {
    setItemsPerPage(n);
    setCurrentPage(1);
  };

  const exportExtraParams = useMemo(() => {
    const params: Record<string, any> = {};
    if (statusFilter === 'unused') {
      params.unused = 'true';
      params.status = 'unused';
    } else if (statusFilter === 'used') {
      params.unused = 'false';
      params.status = 'used';
    }

    const dbNames = selectedDatabases
      .map((item) => (typeof item === 'string' ? item : item?.id || item?.name || item?.database))
      .filter((name) => name && name !== '*');

    if (dbNames.length > 0) {
      params.database = dbNames.join(',');
    }

    if (debouncedSearch.trim()) {
      params.search = debouncedSearch.trim();
    }

    return params;
  }, [statusFilter, selectedDatabases, debouncedSearch]);

  const labels: string[] = graphData?.labels || [];
  const series: Record<string, (number | null)[]> = graphData?.series || {};
  const groups: string[] = graphData?.groups || Object.keys(series);
  const summary = graphData?.summary || {};

  const formatTimeLabels = (lbls: string[]) => {
    if (!lbls || lbls.length === 0) return [];
    const minutes = parseInt(timeRange || '60', 10);
    const seen: { [key: string]: number } = {};
    return lbls.map((lbl) => {
      const d = new Date(lbl);
      let base = isNaN(d.getTime())
        ? lbl
        : minutes > 1440
          ? d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
          : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

      if (seen[base] !== undefined) {
        seen[base] += 1;
        base = `${base}${' '.repeat(seen[base])}`;
      } else {
        seen[base] = 0;
      }
      return base;
    });
  };

  const formattedGraphData = useMemo(() => {
    if (!labels.length) return [];
    const timeLabels = formatTimeLabels(labels);
    return labels.map((_, idx) => {
      const point: Record<string, any> = { time: timeLabels[idx] };
      groups.forEach((grp) => {
        const rawVal = series[grp]?.[idx];
        point[grp] = rawVal !== null && rawVal !== undefined ? Number(rawVal) : 0;
      });
      return point;
    });
  }, [labels, series, groups, timeRange]);

  const activeGroups = useMemo(() => {
    return groups.filter((grp) => {
      const arr = series[grp] || [];
      return arr.some((v) => v !== null && v !== undefined && v > 0);
    });
  }, [groups, series]);

  const indexDropdownOptions = useMemo(() => {
    return [
      { label: `All Active Indexes (${activeGroups.length > 0 ? activeGroups.length : groups.length})`, value: 'ALL' },
      ...groups.map((grp) => ({ label: grp, value: grp })),
    ];
  }, [groups, activeGroups]);

  const visibleGroups = useMemo(() => {
    if (selectedIndexFilter === 'ALL') {
      return activeGroups.length > 0 ? activeGroups : groups.slice(0, 10);
    }
    return [selectedIndexFilter];
  }, [selectedIndexFilter, activeGroups, groups]);

  const hasGraphData = useMemo(() => {
    return formattedGraphData.length > 0 && groups.length > 0;
  }, [formattedGraphData, groups]);

  const topIndexInfo = useMemo(() => {
    const peakMap = summary?.peak || {};
    let maxPeak = 0;
    let topIndex = 'None';
    Object.keys(peakMap).forEach((k) => {
      if (Number(peakMap[k]) > maxPeak) {
        maxPeak = Number(peakMap[k]);
        topIndex = k;
      }
    });
    return { topIndex, maxPeak };
  }, [summary]);

  const getOpacity = (hovered: string | null, key: string) => {
    if (hovered === null) return 1;
    return hovered === key ? 1 : 0.12;
  };



  return (
    <div className="space-y-4 relative">
      {/* Top Banner Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 rounded-lg bg-orange-500 text-white shadow-md">
            <TableOfContents className="w-4 h-4" />
          </div>
          <div>
            <h2 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white tracking-wide flex items-center gap-2">
              <span>Indexes Diagnostics & Performance</span>
              {isMySQL && (
                <span className="px-2 py-0.5 text-[10px] font-bold uppercase rounded-md bg-orange-50 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400 border border-orange-200 dark:border-orange-500/30">
                  {effectiveDbType}
                </span>
              )}
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {isClickHouse
                ? 'Index definitions and how well queries use them'
                : 'Real-time index scan efficiency, cache hit telemetry, and usage patterns'}
            </p>
          </div>
        </div>
      </div>

      {/* MySQL Index Usage Graph Section */}
      {isMySQL && graphData?.applicable && (
        <div className="space-y-3.5">
          {/* 4 Index KPI Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
            {/* Total Operations */}
            <div className="bg-blue-50/70 dark:bg-blue-600/10 border border-blue-200 dark:border-blue-500/30 rounded-xl p-3 shadow-sm dark:shadow-md">
              <div className="flex items-center justify-between text-[11px] text-gray-600 dark:text-gray-400 font-semibold mb-1">
                <span>Total Index Operations</span>
                <TrendingUp className="w-4 h-4 text-blue-500 dark:text-blue-400" />
              </div>
              <div className="text-xl font-bold font-mono text-gray-900 dark:text-white">
                {summary?.total ?? (activeGroups.length > 0 ? 'Active' : 0)}
              </div>
              <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">Across monitored indexes</div>
            </div>

            {/* Top Active Index */}
            <div className="bg-orange-50/70 dark:bg-orange-600/10 border border-orange-200 dark:border-orange-500/30 rounded-xl p-3 shadow-sm dark:shadow-md">
              <div className="flex items-center justify-between text-[11px] text-gray-600 dark:text-gray-400 font-semibold mb-1">
                <span>Top Active Index</span>
                <Zap className="w-4 h-4 text-orange-500 dark:text-orange-400" />
              </div>
              <div className="text-sm font-bold font-mono text-orange-600 dark:text-orange-300 truncate" title={topIndexInfo.topIndex}>
                {topIndexInfo.topIndex}
              </div>
              <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">
                Peak: <span className="font-mono text-gray-900 dark:text-white font-bold">{topIndexInfo.maxPeak} ops</span>
              </div>
            </div>

            {/* Peak Activity */}
            <div className="bg-purple-50/70 dark:bg-purple-600/10 border border-purple-200 dark:border-purple-500/30 rounded-xl p-3 shadow-sm dark:shadow-md">
              <div className="flex items-center justify-between text-[11px] text-gray-600 dark:text-gray-400 font-semibold mb-1">
                <span>Peak Index Rate</span>
                <Activity className="w-4 h-4 text-purple-500 dark:text-purple-400" />
              </div>
              <div className="text-xl font-bold font-mono text-purple-600 dark:text-purple-300">
                {topIndexInfo.maxPeak} <span className="text-xs text-gray-500 dark:text-gray-400 font-normal">ops/interval</span>
              </div>
              <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">Max recorded spike</div>
            </div>

            {/* Total Tracked Indexes */}
            <div className="bg-emerald-50/70 dark:bg-emerald-600/10 border border-emerald-200 dark:border-emerald-500/30 rounded-xl p-3 shadow-sm dark:shadow-md">
              <div className="flex items-center justify-between text-[11px] text-gray-600 dark:text-gray-400 font-semibold mb-1">
                <span>Tracked Indexes</span>
                <Layers className="w-4 h-4 text-emerald-500 dark:text-emerald-400" />
              </div>
              <div className="text-xl font-bold font-mono text-emerald-600 dark:text-emerald-400">
                {groups.length}
              </div>
              <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
                {activeGroups.length} actively utilized
              </div>
            </div>
          </div>

          {/* Index Usage Line Chart Card */}
          <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 border-b border-gray-100 dark:border-gray-700/50 pb-2.5">
              <div className="flex items-center gap-2">
                <BarChart2 className="w-4 h-4 text-orange-500 dark:text-orange-400" />
                <h3 className="text-xs font-bold text-gray-800 dark:text-white uppercase tracking-wider">
                  Index Usage Trend
                </h3>
              </div>

              {/* Index Filter Dropdown & Refresh */}
              <div className="flex items-center gap-2">
                {groups.length > 0 && (
                  <CustomDropdown
                    data={indexDropdownOptions}
                    selectedValue={selectedIndexFilter}
                    onSelect={(val) => setSelectedIndexFilter(val)}
                    width="w-48 sm:w-56"
                    placeholder="Select Index"
                    buttonHeight={8}
                  />
                )}
                <button
                  onClick={fetchIndexGraph}
                  type="button"
                  className="p-1.5 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white border border-gray-200 dark:border-gray-700 rounded-lg transition-colors cursor-pointer shadow-sm"
                  title="Refresh graph"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${graphLoading ? 'animate-spin text-orange-500 dark:text-orange-400' : ''}`} />
                </button>
              </div>
            </div>

            {/* Recharts Area */}
            <div className="h-56 w-full flex items-center justify-center">
              {graphLoading ? (
                <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                  <Loader2 className="w-4 h-4 text-orange-500 dark:text-orange-400 animate-spin" />
                  <span>Loading index telemetry...</span>
                </div>
              ) : hasGraphData ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={formattedGraphData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#9ca3af" strokeOpacity={0.2} />
                    <XAxis dataKey="time" stroke="#6b7280" fontSize={10} tickLine={false} />
                    <YAxis stroke="#6b7280" fontSize={10} tickLine={false} tickCount={4} />
                    <Tooltip content={<ChartTooltip />} />
                    <Legend
                      content={
                        <ScrollableLegend
                          hoveredSeries={hoveredSeries}
                          onHoverSeries={setHoveredSeries}
                        />
                      }
                    />
                    {visibleGroups.map((grpKey, idx) => {
                      const origIdx = groups.indexOf(grpKey);
                      const color = PALETTE[(origIdx >= 0 ? origIdx : idx) % PALETTE.length];
                      const isHovered = hoveredSeries === grpKey;

                      return (
                        <Line
                          key={grpKey}
                          type="monotone"
                          dataKey={grpKey}
                          name={grpKey}
                          stroke={color}
                          strokeOpacity={getOpacity(hoveredSeries, grpKey)}
                          strokeWidth={isHovered ? 2.5 : 1.5}
                          dot={false}
                        />
                      );
                    })}
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-6 text-xs text-gray-400 dark:text-gray-500 italic">
                  No index usage data recorded for the selected time range.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Index Table Section with Filters */}
      <div className="space-y-3">
        {/* Filter Controls & Search */}
        <div className="flex flex-wrap items-center justify-between gap-2.5">
          <div className="flex items-center gap-2">
            <TableOfContents className="w-4 h-4 text-orange-500 dark:text-orange-400" />
            <h3 className="text-xs font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wider">
              Index Table & Diagnostics
            </h3>
          </div>

          <div className="flex flex-wrap items-center gap-2.5">
            {/* Database Multi-Select Dropdown */}
            <div className="w-full sm:w-60">
              <MultiSelectApiDropdownWithAll
                placeholder="Select Databases"
                apiEndpoint="/db_monitor_dashboard/databases"
                additionalParams={{
                  cluster_id: activeCluster,
                  ...(clientId ? { client_id: clientId } : {}),
                }}
                displayProperty="name"
                idProperty="id"
                searchFields={['name']}
                value={selectedDatabases}
                onChange={(items) => {
                  setSelectedDatabases(items);
                  setCurrentPage(1);
                }}
                selectAll={true}
                buttonHeight={40}
              />
            </div>

            {/* Search Box */}
            <div className="relative w-full sm:w-56">
              <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search index, table..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-white dark:bg-gray-900/90 border border-gray-200 dark:border-gray-700 rounded-lg pl-8 pr-7 py-1 text-xs text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-orange-500 h-8 transition-colors"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-white cursor-pointer"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Status Filter Buttons */}
            <div className="flex items-center bg-gray-100 dark:bg-gray-900/90 border border-gray-200 dark:border-gray-700 rounded-lg p-0.5 h-8">
              <button
                onClick={() => handleStatusFilterChange('all')}
                className={`px-2.5 py-1 text-[11px] font-semibold rounded-md transition-colors cursor-pointer ${statusFilter === 'all'
                    ? 'bg-orange-500 text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
              >
                All
              </button>
              <button
                onClick={() => handleStatusFilterChange('used')}
                className={`px-2.5 py-1 text-[11px] font-semibold rounded-md transition-colors cursor-pointer ${statusFilter === 'used'
                    ? 'bg-emerald-600 text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
              >
                Active
              </button>
              <button
                onClick={() => handleStatusFilterChange('unused')}
                className={`px-2.5 py-1 text-[11px] font-semibold rounded-md transition-colors cursor-pointer ${statusFilter === 'unused'
                    ? 'bg-amber-600 text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
              >
                Unused
              </button>
            </div>

            {/* Export Modal */}
            <DBExportModal
              endpoint="/db_monitor_dashboard/indexes/table"
              columns={indexExportColumns}
              cluster_id={activeCluster}
              client_id={clientId}
              extraParams={exportExtraParams}
              defaultSortBy="idx_scans"
              defaultSortOrder="desc"
              buttonText="Export"
              title="Export Indexes"
              subtitle="Export database index usage telemetry and statistics"
            />
          </div>
        </div>
      </div>

      {/* Main Table Card */}
      <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl overflow-hidden shadow-sm dark:shadow-xl flex flex-col relative">
        {loading && (
          <div className="absolute inset-0 bg-white/60 dark:bg-gray-900/50 backdrop-blur-[1px] z-20 flex items-center justify-center">
            <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
          </div>
        )}

        {/* Scrollable Table Area */}
        <div className="overflow-x-auto overflow-y-auto custom-scroll max-h-[calc(100vh-250px)]">
          <table className="w-full text-left text-xs border-collapse">
            <thead className="sticky top-0 z-10">
              <tr className="border-b border-gray-200 dark:border-gray-700/80 bg-gray-50 dark:bg-gray-900 shadow-sm text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wider font-semibold whitespace-nowrap">
                <th className="py-2 px-3 bg-gray-50 dark:bg-gray-900">{isClickHouse ? 'Database' : 'Database / Schema'}</th>
                <th className="py-2 px-3 bg-gray-50 dark:bg-gray-900">Table</th>
                <th className="py-2 px-3 bg-gray-50 dark:bg-gray-900">Index Name</th>
                <th className="py-2 px-3 bg-gray-50 dark:bg-gray-900 text-right">{isClickHouse ? 'Queries' : 'Scans'}</th>
                <th className="py-2 px-3 bg-gray-50 dark:bg-gray-900 text-right">{isClickHouse ? 'Rows Read' : 'Tuples Read'}</th>
                {
                  !isMariaDB && (
                    <th className="py-2 px-3 bg-gray-50 dark:bg-gray-900 text-right">{isClickHouse ? 'Rows Returned' : 'Tuples Fetch'}</th>
                  )}
                {!isClickHouse && !isMariaDB && (
                  <th className="py-2 px-3 bg-gray-50 dark:bg-gray-900 text-right">Blocks Hit / Read</th>
                )}
                {!isClickHouse && !isMariaDB && (
                  <th className="py-2 px-3 bg-gray-50 dark:bg-gray-900 text-right">Cache Hit %</th>
                )}

                {!isMariaDB && (
                  <th className="py-2 px-3 bg-gray-50 dark:bg-gray-900 text-right">{isClickHouse ? 'Rows Read / Query' : 'Rows / Scan'}</th>
                )}
                <th className="py-2 px-3 bg-gray-50 dark:bg-gray-900 text-center">Status</th>

              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/30 text-gray-800 dark:text-gray-200 bg-white dark:bg-gray-900/30">
              {indexData && indexData.length > 0 ? (
                indexData.map((row, idx) => {
                  const cachePct = row.cache_hit_pct;
                  const rowsPerScan = row.rows_per_scan;

                  return (
                    <tr key={`${row.database}-${row.table}-${row.index}-${idx}`} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                      {/* Database / Schema */}
                      <td className="py-2 px-3 font-mono text-[11px] whitespace-nowrap">
                        <div className="flex items-center gap-1.5 text-gray-800 dark:text-gray-300">
                          <Database className="w-3.5 h-3.5 text-orange-500 dark:text-orange-400 shrink-0" />
                          <span>{row.database || '-'}</span>
                          {!isClickHouse && (
                            <>
                              <span className="text-gray-400 dark:text-gray-500">/</span>
                              <span className="text-gray-500 dark:text-gray-400">{row.schema || 'public'}</span>
                            </>
                          )}
                        </div>
                      </td>

                      {/* Table */}
                      <td className="py-2 px-3 font-mono text-[11px] text-gray-800 dark:text-gray-300 whitespace-nowrap">
                        {row.table || '-'}
                      </td>

                      {/* Index Name */}
                      <td className="py-2 px-3 font-mono text-[11px] text-blue-600 dark:text-blue-300 font-semibold max-w-[200px] truncate" title={row.index}>
                        {row.index || '-'}
                      </td>

                      {/* Scans */}
                      <td className="py-2 px-3 text-right font-mono text-[11px] text-gray-900 dark:text-gray-200">
                        {(row.idx_scans || 0).toLocaleString()}
                      </td>

                      {/* Tuples Read */}
                      <td className="py-2 px-3 text-right font-mono text-[11px] text-gray-700 dark:text-gray-300">
                        {(row.idx_tup_read || 0).toLocaleString()}
                      </td>

                      {/* Tuples Fetch */}
                      {!isMariaDB && (
                        <td className="py-2 px-3 text-right font-mono text-[11px] text-gray-700 dark:text-gray-300">
                          {(row.idx_tup_fetch || 0).toLocaleString()}
                        </td>
                      )}

                      {/* Blocks Hit / Read */}
                      {!isClickHouse && !isMariaDB && (
                        <td className="py-2 px-3 text-right font-mono text-[11px] text-gray-700 dark:text-gray-300 whitespace-nowrap">
                          <span className="text-emerald-600 dark:text-emerald-400">{(row.idx_blks_hit || 0).toLocaleString()}</span>
                          <span className="text-gray-400 dark:text-gray-600 mx-1">/</span>
                          <span className="text-amber-600 dark:text-amber-400">{(row.idx_blks_read || 0).toLocaleString()}</span>
                        </td>
                      )}

                      {/* Cache Hit % */}
                      {!isClickHouse && !isMariaDB && (
                        <td className="py-2 px-3 text-right font-mono text-[11px]">
                          {cachePct !== null && cachePct !== undefined ? (
                            <span
                              className={
                                cachePct >= 95
                                  ? 'text-emerald-600 dark:text-emerald-400 font-semibold'
                                  : cachePct >= 80
                                    ? 'text-amber-600 dark:text-amber-400'
                                    : 'text-rose-600 dark:text-rose-400'
                              }
                            >
                              {typeof cachePct === 'number' ? `${cachePct.toFixed(1)}%` : `${cachePct}%`}
                            </span>
                          ) : (
                            <span className="text-gray-400 dark:text-gray-500">-</span>
                          )}
                        </td>
                      )}

                      {/* Rows Per Scan */}
                      {!isMariaDB && (
                        <td className="py-2 px-3 text-right font-mono text-[11px] text-gray-700 dark:text-gray-300">
                          {rowsPerScan !== null && rowsPerScan !== undefined ? (
                            typeof rowsPerScan === 'number' ? rowsPerScan.toFixed(1) : rowsPerScan
                          ) : (
                            <span className="text-gray-400 dark:text-gray-500">-</span>
                          )}
                        </td>
                      )}

                      {/* Status / Unused */}
                      <td className="py-2 px-3 text-center whitespace-nowrap">
                        {row.unused ? (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono font-bold bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-500/30">
                            <AlertTriangle className="w-3 h-3 text-amber-500 dark:text-amber-400 shrink-0" />
                            Unused
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono font-bold bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/30">
                            <CheckCircle2 className="w-3 h-3 text-emerald-500 dark:text-emerald-400 shrink-0" />
                            Active
                          </span>
                        )}
                      </td>

                      {/* Last Seen */}
                     
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={isClickHouse ? 9 : 11} className="py-12 text-center text-gray-400 dark:text-gray-500 italic">
                    {searchQuery
                      ? `No indexes matching "${searchQuery}".`
                      : 'No index diagnostics telemetry available.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          itemsPerPage={itemsPerPage}
          totalItems={totalItems}
          onItemsPerPageChange={handleItemsPerPageChange}
          itemsPerPageOptions={[10, 25, 50, 100]}
        />
      </div>
    </div>
  );
};

export default IndexesView;

